import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard, SecondaryButton, SegmentedControl } from "@/components/ui";
import {
  actionAuditEventSummary,
  actionAuditReferenceDate,
  groupActionAuditEvents,
  type ActionAuditEvent,
  type ActionAuditScope,
} from "@/models/actionAudit";
import { actionAuditScopes } from "@/models/featureAccessPolicy";
import { activeMembership, resolvedLeagueID } from "@/models/user";
import {
  actionAuditScopeTitle,
  eventMatchesActionAuditScopes,
  loadActionAuditEvents,
} from "@/services/actionAudit";
import { relativeActionAuditTime } from "./activityHelpers";
import "./activity.css";

function scopeKey(scope: ActionAuditScope, index: number): string {
  return `${actionAuditScopeTitle(scope)}:${index}`;
}

function eventIcon(event: ActionAuditEvent): string {
  switch (event.actionType) {
    case "create":
      return "+";
    case "update":
      return "✎";
    case "archive":
      return "▣";
    case "undo":
      return "↶";
  }
}

function ActivityEventRow({ event }: { event: ActionAuditEvent }) {
  return (
    <Link className="activity-event-row" to={`/home/activity/${encodeURIComponent(event.id)}`}>
      <span className={`activity-event-icon activity-event-icon-${event.actionType}`} aria-hidden="true">
        {eventIcon(event)}
      </span>
      <span className="activity-event-copy">
        <strong>{actionAuditEventSummary(event)}</strong>
        <span>{relativeActionAuditTime(actionAuditReferenceDate(event))}</span>
      </span>
      <span className="activity-event-badges">
        {event.actorKind === "aiAssistant" && <span className="badge badge-activity-ai">AI</span>}
        {event.actorKind === "externalAgent" && <span className="badge badge-activity-ai">Agent</span>}
        {event.actionType === "undo" && <span className="badge">Undo</span>}
        <span className="activity-chevron" aria-hidden="true">›</span>
      </span>
    </Link>
  );
}

export function ActivityListView() {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const membership = currentUser ? activeMembership(currentUser) : undefined;
  const leagueID =
    currentUser?.activeContext?.leagueID
    ?? (currentUser?.activeContext?.targetType === "league" ? currentUser.activeContext.targetID : undefined)
    ?? (membership ? resolvedLeagueID(membership) : undefined);
  const scopes = useMemo(
    () => actionAuditScopes(currentUser, isSBotLabsAdmin, leagueID),
    [currentUser, isSBotLabsAdmin, leagueID],
  );
  const scopeOptions = useMemo(
    () => scopes.map((scope, index) => ({ value: scopeKey(scope, index), label: actionAuditScopeTitle(scope) })),
    [scopes],
  );
  const [selectedScopeKey, setSelectedScopeKey] = useState("");
  const [events, setEvents] = useState<ActionAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scopeOptions.length) {
      setSelectedScopeKey("");
      return;
    }
    if (!scopeOptions.some((option) => option.value === selectedScopeKey)) {
      setSelectedScopeKey(scopeOptions[0].value);
    }
  }, [scopeOptions, selectedScopeKey]);

  const loadEvents = useCallback(async () => {
    if (!scopes.length) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setEvents(await loadActionAuditEvents(scopes));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Recent activity could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [scopes]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const selectedScopeIndex = scopeOptions.findIndex((option) => option.value === selectedScopeKey);
  const selectedScope = scopes[selectedScopeIndex >= 0 ? selectedScopeIndex : 0];
  const visibleEvents = selectedScope
    ? events.filter((event) => eventMatchesActionAuditScopes(event, [selectedScope]))
    : events;
  const groups = groupActionAuditEvents(visibleEvents);

  return (
    <div className="activity-screen">
      <SBCard className="activity-intro">
        <div className="activity-intro-main">
          <span className="activity-intro-icon" aria-hidden="true">↻</span>
          <div>
            <h1>Recent Activity</h1>
            <p>Data changes from the last 24 hours. Open a change to see details and undo individual fields while they’re still in the window.</p>
          </div>
        </div>
        <div className="activity-refresh">
          <SecondaryButton title={loading ? "Refreshing…" : "Refresh"} onClick={() => void loadEvents()} disabled={loading} />
        </div>
        <ErrorText>{error}</ErrorText>
      </SBCard>

      {scopeOptions.length > 1 && selectedScopeKey && (
        <SegmentedControl options={scopeOptions} value={selectedScopeKey} onChange={setSelectedScopeKey} />
      )}

      {loading && events.length === 0 ? (
        <SBCard><LoadingView label="Loading activity…" /></SBCard>
      ) : groups.length === 0 ? (
        <SBCard className="activity-empty">
          <span className="activity-empty-icon" aria-hidden="true">□</span>
          <div>
            <h2>No recent activity</h2>
            <p>Changes made in the last 24 hours will appear here.</p>
          </div>
        </SBCard>
      ) : (
        groups.map((group) => (
          <section className="activity-group" key={group.title}>
            <h2>{group.title}</h2>
            <SBCard className="activity-list-card">
              {group.events.map((event) => <ActivityEventRow event={event} key={event.id} />)}
            </SBCard>
          </section>
        ))
      )}
    </div>
  );
}
